import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "../components/LandingPageNew/Navbar";

const STORAGE_KEY = "howRemoveFlow_v1";

function loadFlow() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveFlow(next) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {}
}

function useHowRemoveFlow() {
  const [flow, setFlow] = useState(() => loadFlow());
  const updateFlow = (patch) => {
    setFlow((prev) => {
      const next = { ...prev, ...patch };
      saveFlow(next);
      return next;
    });
  };
  const resetFlow = () => {
    setFlow({});
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {}
  };
  return { flow, updateFlow, resetFlow };
}

const StepCircle = ({ number, text, active }) => (
  <div className="flex flex-col items-center gap-3 min-w-[90px] text-center">
    <div
      className={`w-[60px] h-[60px] flex items-center justify-center rounded-full text-white text-[24px] font-semibold ${
        active ? "bg-[#0b2b40]" : "bg-[#0b2b40]/30"
      }`}
    >
      {number}
    </div>
    <p
      className={`text-[16px] font-medium ${
        active ? "text-[#0b2b40]" : "text-[#0b2b40]/40"
      }`}
    >
      {text}
    </p>
  </div>
);

const StepLine = ({ active, mobile }) => (
  <div
    className={`rounded-full ${
      mobile ? "w-[35px] h-[4px]" : "w-[130px] h-[4px]"
    } ${
      active
        ? "bg-gradient-to-r from-[#0b2b40] to-white"
        : "bg-gradient-to-r from-[#0b2b40]/30 to-white/30"
    }`}
  />
);

export default function HowRemove1() {
  const navigate = useNavigate();
  const location = useLocation();
  const { flow, updateFlow } = useHowRemoveFlow();

  // places can come from:
  // 1) location.state?.places (your current behavior)
  // 2) session storage (if user refreshed)
  const initialPlaces = useMemo(() => {
    return location.state?.places || flow.places || [];
  }, [location.state, flow.places]);

  const [places, setPlaces] = useState(initialPlaces);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    // keep places synced in storage
    updateFlow({ places });
    
  }, [places]);

  useEffect(() => {
    if (location.state?.places) {
      setPlaces(location.state.places);
    }
  }, [location.state]);

  const handlePlaceSelect = (place) => {
    // when selecting a place, reset downstream selections
    updateFlow({
      business: place,
      reviews: [],
      starCount: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
      selectedReviews: [],
      selectedStars: [],
      activeTier: 0,
      pricePerDeletion: 129.0,
    });

    navigate("/Bewertungsmanager-2");
  };

  return (
    <>
      <Navbar />
      <section className="relative bg-cover bg-no-repeat font-sans py-4 md:py-8 px-4">
        <div className="flex justify-center mt-14">
          <div className="relative w-full max-w-[1300px] mx-auto p-2 lg:p-10 flex flex-col gap-8">
            <div className="flex w-full gap-10">
              <div className="flex flex-col gap-8 transition-all duration-300 w-full">
                {/* Company Header */}
                <div className="flex flex-col gap-3 mb-10 mt-10 items-center text-center">
                  <h2 className="h2-mb h2-md h2-lg h2-2xl">
                    Unfaire Bewertung(en)? <br />
                    <span className="text-[#0b2b40]">
                      Wie Sie die Löschung einleiten:
                    </span>
                  </h2>
                  <p className="text-gray-500 text-sm md:text-lg w-11/12 md:w-11/12 mt-2 text-center">
                    Unser Bewertungsmanager gibt Ihnen die Kontrolle über Ihren
                    Ruf zurück - ganz risikofrei, denn Sie zahlen nur bei
                    erfolgreicher Löschung.
                  </p>
                </div>

                {/* STEPS */}
                <div className="hidden lg:flex justify-center mt-2 gap-10 items-center">
                  <StepCircle active number="1" text="Firmenname eingeben" />
                  <StepLine active />
                  <StepCircle active={false} number="2" text="Negative Bewertung auswählen" />
                  <StepLine active={false} />
                  <StepCircle active={false} number="3" text="Vorgang starten" />
                </div>

                <div className="lg:hidden flex justify-center mt-2 gap-6">
                  <StepCircle active number="1" text="Firmenname eingeben" />
                  <StepLine active mobile />
                  <StepCircle active={false} number="2" text="Negative Bewertung auswählen" />
                  <StepLine active={false} mobile />
                  <StepCircle active={false} number="3" text="Vorgang starten" />
                </div>

                {/* STEP 1 CONTENT */}
                <div className="w-full mx-auto mt-10">
                  <div className="border rounded-2xl p-6 bg-white">
                    <div className="flex justify-between items-center pb-4 border-b">
                      <h2 className="text-xl font-semibold">Suchergebnisse</h2>
                      <span className="text-sm text-gray-500">
                        {places.length} Ergebnis{places.length !== 1 && "se"} gefunden
                      </span>
                    </div>

                    <div className="mt-4 space-y-4">
                      {places.map((place) => (
                        <div
                          key={place.id}
                          className="flex flex-col md:flex-row justify-between items-left md:items-center p-4 rounded-xl hover:bg-gray-50 transition"
                        >
                          <div className="flex flex-col gap-1">
                            <h3 className="font-semibold text-gray-900">
                              {place?.name?.text}
                            </h3>
                            <p className="text-sm text-gray-500">{place.address}</p>
                          </div>

                          <div className="flex flex-col md:flex-row items-left md:items-center gap-6">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-900">
                                {place.rating?.toFixed(1)}
                              </span>
                              <span className="text-yellow-400 text-lg">
                                {"★".repeat(Math.round(place.rating || 0))}
                                {"☆".repeat(5 - Math.round(place.rating || 0))}
                              </span>
                              <span>({place.totalReviews})</span>
                            </div>

                            <button
                              onClick={() => handlePlaceSelect(place)}
                              className="bg-[#0b2b40] text-white px-6 py-2 rounded-full text-sm font-medium hover:opacity-90 transition"
                            >
                              Auswählen
                            </button>
                            <hr />
                          </div>
                        </div>
                      ))}

                      {places.length === 0 && (
                        <p className="text-center text-gray-500 py-10">
                          Keine Ergebnisse vorhanden (places ist leer).
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                {/* END STEP 1 */}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
